/* eslint-disable complexity */
import React, { FC, Fragment, useMemo } from 'react';
import { Box, ButtonBase, Divider, Drawer, Stack, Typography } from '@mui/material';
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useLocalization } from '@/data/Localization';
import { HeaderMiniCartDropMenuItem } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItem';
import ClearIcon from '@mui/icons-material/Clear';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { useOrderItemListRowsAvailability } from '@/data/Content/OrderItemInventory';
import { useNotifications } from '@/data/Content/Notifications';
import { useMobileHeaderQuery } from '..';
import { Img } from '@/components/blocks/MaterialImage';
import { useBasicCheckOut } from '@/data/Content/BasicCheckOut';
import { OrderSummaryBreakdown } from '@/components/content/Cart/parts/OrderSummaryBreakdown';

export const HeaderMiniCartDrawerMenu: FC<{
	fromLocal: boolean;
	onClose: () => void;
	// eslint-disable-next-line complexity
}> = ({ fromLocal, onClose }) => {
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, checkout, canContinue, getCount, router } = useCart();
	const isMobile = useMobileHeaderQuery();
	const MAX_ROWS = isMobile ? 2 : 3;
	const { firstNRows } = useMemo(() => {
		const firstNRows = orderItems?.slice(0, MAX_ROWS);
		const firstNRowsCount = getCount(firstNRows);
		return { firstNRows, firstNRowsCount };
	}, [MAX_ROWS, getCount, orderItems]);

	const { showSuccessMessage } = useNotifications();

	const { totalCoupons, totalDiscount, totalPriceWithoutOffer, orderItemListRowsSorted } =
		useBasicCheckOut({
			adjustment: order?.adjustment || [],
			orderItem: order?.orderItem || [],
			orderId: order?.orderId || '',
		});

	const { grandTotal = '0', totalShippingCharge = '0' } = order || {};

	const {
		orderItemListRowsWithAvailability,
		orderItemListRowsNotAvailable,
		loadingOrderItemListRowsAvailability,
	} = useOrderItemListRowsAvailability(orderItemListRowsSorted);

	const loading =
		orderItemListRowsWithAvailability.length === 0 || loadingOrderItemListRowsAvailability;

	const disabled = loading ? true : canContinue ? !canContinue() : false;

	const handleOnClickCheckout = () => {
		if (orderItemListRowsNotAvailable.length > 0) {
			router.push(RouteLabels.Cart.route.t()).finally(() => {
				showSuccessMessage(
					'Revisa tus productos del carrito y elimina el producto caducado para continuar tu compra.',
					'TIENES UN PRODUCTO CADUCADO'
				);
			});

			return;
		}

		checkout();
	};

	const handleOnClickCart = () => {
		const path = window.location.pathname;
		if (path.includes(RouteLabels.Cart.route.t())) {
			onClose();
			return;
		}
		router.push(RouteLabels.Cart.route.t());
	};

	return (
		<Drawer
			open
			onClose={onClose}
			ModalProps={{
				keepMounted: true,
				disableScrollLock: false,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					height: 'auto',
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 1000,
					borderTopLeftRadius: '10px',
					borderTopRightRadius: '10px',
					backgroundColor: '#FFFFFF',
				}}
			>
				{firstNRows?.length > 0 ? (
					<Fragment>
						<ClearIcon
							sx={{
								fontSize: 'var(--font-size-xl)',
								cursor: 'pointer',
								position: 'absolute',
								top: '14px',
								right: '14px',
							}}
							onClick={onClose}
						/>
						<Box
							sx={{
								width: '100%',
								paddingTop: '30px',
								paddingX: '30px',
								paddingBottom: '38px',
							}}
						>
							<Typography
								sx={{
									fontSize: 'var(--font-size-lg)',
									color: '#2C2C2C',
									fontWeight: 800,
									display: 'flex',
									alignItems: 'center',
									marginBottom: '20px',
									textTransform: 'uppercase',
								}}
							>
								{!fromLocal ? (
									<Img
										src="/images/check-circle.png"
										alt="check-circle"
										sx={{ height: '27px', width: '27px', marginRight: '11px' }}
									/>
								) : null}
								{fromLocal ? 'Carrito de compras' : 'AGREGASTE AL CARRITO'}
							</Typography>
							<Stack
								spacing={1}
								sx={{
									maxHeight: 250,
									overflowY: 'auto',
								}}
							>
								{!loading ? (
									<React.Fragment>
										{orderItemListRowsWithAvailability.map((product) => (
											<HeaderMiniCartDropMenuItem
												key={product.orderItemListRow.orderItem.orderItemId}
												thumbnail={product.details.thumbnail}
												name={product.details.name}
												attributes={product.details.attributes}
												offerPrice={product.details.prices.offer || 0}
												displayPrice={product.details.prices.list || 0}
												totalAdjustment={product.orderItemListRow.totalAdjustment.totalAdjustment}
												orderItemPrice={product.orderItemListRow.price.orderItemPrice}
												exhausted={product.availability?.status === false}
												variant="drawer"
											/>
										))}
									</React.Fragment>
								) : (
									<HeaderMiniCartDropMenuItemSkeleton variant="drawer" />
								)}
							</Stack>
							<Divider sx={{ marginTop: '20px', marginBottom: '11px' }} />
							<OrderSummaryBreakdown
								showTitle={false}
								total={grandTotal}
								shipping={totalShippingCharge}
								discount={totalDiscount}
								subtotal={totalPriceWithoutOffer}
								coupons={totalCoupons}
								isMiniCart
							/>
							<Stack
								direction="row"
								spacing="14px"
								alignItems="center"
								justifyContent={'center'}
								sx={{
									marginTop: '30px',
								}}
							>
								<ButtonBase
									sx={{
										backgroundColor: '#F1F1F1',
										color: '#2C2C2C',
										borderRadius: 50,
										height: '46px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontWeight: 600,
										fontSize: 'var(--font-size-body)',
										minWidth: '48%',
									}}
									onClick={handleOnClickCart}
								>
									{`Ver carrito ${
										orderItemListRowsWithAvailability.length > 0
											? `(${orderItemListRowsWithAvailability.length})`
											: ''
									}`}
								</ButtonBase>
								<ButtonBase
									sx={{
										backgroundColor: '#2682E7',
										color: '#FFFFFF',
										borderRadius: 50,
										height: '46px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontWeight: 600,
										minWidth: '48.5%',
										fontSize: 'var(--font-size-body)',
										':disabled': {
											backgroundColor: '#B5B5B5',
											color: '#FFFFFF',
										},
									}}
									disabled={disabled}
									onClick={handleOnClickCheckout}
								>
									{'Ir a pagar'}
								</ButtonBase>
							</Stack>
						</Box>
					</Fragment>
				) : (
					<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
						<Typography variant="body1" align="center">
							{CartLabels.Empty.t()}
						</Typography>
					</Stack>
				)}
			</Box>
		</Drawer>
	);
};
